import { useEffect, useState, type ReactElement } from 'react';
import { Anchor, Badge, Box, Button, Divider, Drawer, FileButton, Group, Select, Stack, Switch, Tabs, Text, Textarea, Timeline } from '@mantine/core';
import { CornerDownRight, Download, MessagesSquare, Paperclip, ScrollText, Upload } from 'lucide-react';
import ConversationViewer from '@/shared/surfaces/ConversationViewer';
import type { AssigneeOption, GovernanceIssuesViewModel } from './GovernanceIssuesViewModel';
import type { GovernanceIssueAttachment, GovernanceIssueEvent } from './GovernanceIssuesService';
import { KIND_META, SEVERITY_COLOR } from './IssueVisuals';
import type { IssueStatus } from './PolicyIssues';

function formatWhen(iso: string): string {
    if (!iso) return '—';
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const EVENT_LABEL: Record<GovernanceIssueEvent['kind'], string> = {
    status_changed: 'Status changed',
    flagged: 'Flagged',
    unflagged: 'Unflagged',
    assigned: 'Assigned',
    unassigned: 'Unassigned',
    resolved: 'Resolved',
    reopened: 'Reopened',
    note_added: 'Note added',
    attachment_added: 'File uploaded',
    turn_dismissed: 'Prompt dismissed',
    turn_restored: 'Prompt restored',
};

function eventDetail(event: GovernanceIssueEvent): string {
    const d = event.detail;
    if (typeof d['reason'] === 'string') return d['reason'];
    if (typeof d['filename'] === 'string') return d['filename'];
    if (typeof d['to'] === 'string' && typeof d['from'] === 'string') return `${d['from']} → ${d['to']}`;
    if (typeof d['to'] === 'string') return String(d['to']);
    return '';
}

/** Assignee options, ensuring the current assignee stays selectable even if it isn't in the directory page. */
function assigneeDataFor(current: string | null, options: AssigneeOption[]): AssigneeOption[] {
    if (current && !options.some((o) => o.value === current)) return [{ value: current, label: current }, ...options];
    return options;
}

/** Detail drawer for a single flagged turn: fixed triage header + tabbed conversation / notes / audit. */
export default function GovernanceIssueDetailDrawer({ vm }: { vm: GovernanceIssuesViewModel }): ReactElement {
    const issue = vm.activeIssue;
    const detail = vm.activeDetail;
    const [note, setNote] = useState('');
    const [reason, setReason] = useState('');
    const [resolving, setResolving] = useState(false);
    const [tab, setTab] = useState<string | null>('conversation');
    const [scrollSignal, setScrollSignal] = useState(0);

    useEffect(() => {
        setNote('');
        setReason('');
        setResolving(false);
        setTab('conversation');
        setScrollSignal(0);
    }, [issue?.key]);

    const assigneeData = assigneeDataFor(issue?.assigneeUserId ?? null, vm.assigneeOptions);

    async function downloadAttachment(att: GovernanceIssueAttachment): Promise<void> {
        const content = await vm.fetchAttachment(att.id);
        const bytes = Uint8Array.from(atob(content.contentBase64), (c) => c.charCodeAt(0));
        const url = URL.createObjectURL(new Blob([bytes], { type: content.contentType }));
        const link = document.createElement('a');
        link.href = url;
        link.download = content.filename;
        link.click();
        URL.revokeObjectURL(url);
    }

    const noteCount = detail?.notes.length ?? 0;
    const docCount = detail?.attachments.length ?? 0;
    const eventCount = detail?.events.length ?? 0;

    return (
        <Drawer
            opened={issue !== null}
            onClose={() => vm.closeIssue()}
            position="right"
            size="xl"
            title={issue?.title ?? 'Issue'}
            padding="lg"
        >
            {issue === null ? null : (
                <Stack gap="md">
                    <Group gap="xs">
                        <Badge size="sm" variant="light" color={KIND_META[issue.kind].color}>
                            {KIND_META[issue.kind].label}
                        </Badge>
                        <Badge size="sm" variant="light" color={SEVERITY_COLOR[issue.severity]}>
                            {issue.severity}
                        </Badge>
                        <Text size="xs" c="dimmed">
                            {issue.verdict} · {issue.person} · {formatWhen(issue.timestampIso)}
                        </Text>
                    </Group>

                    <Group grow align="flex-start">
                        <Select
                            size="xs"
                            label="Status"
                            allowDeselect={false}
                            value={issue.status}
                            onChange={(value) => value && void vm.setStatus(issue, value as IssueStatus)}
                            data={[
                                { value: 'open', label: 'Open' },
                                { value: 'investigating', label: 'Investigating' },
                                { value: 'resolved', label: 'Resolved' },
                            ]}
                        />
                        <Select
                            size="xs"
                            label="Assignee"
                            placeholder="Unassigned"
                            searchable
                            clearable
                            nothingFoundMessage="No matching users"
                            data={assigneeData}
                            value={issue.assigneeUserId}
                            onChange={(value) => void vm.assign(issue, value)}
                        />
                    </Group>

                    {issue.status === 'resolved' && detail?.resolutionReason ? (
                        <Text size="xs" c="dimmed">
                            Resolution: {detail.resolutionReason}
                        </Text>
                    ) : resolving ? (
                        <Group align="flex-end" gap="xs">
                            <Textarea flex={1} size="xs" autosize minRows={1} label="Resolution reason" value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
                            <Button size="xs" disabled={reason.trim().length === 0} onClick={() => void vm.resolve(issue, reason.trim())}>
                                Resolve
                            </Button>
                        </Group>
                    ) : (
                        <Button size="compact-xs" variant="subtle" w="fit-content" onClick={() => setResolving(true)}>
                            Resolve with reason…
                        </Button>
                    )}

                    <Switch label="Flagged for attention" checked={issue.flagged} onChange={(e) => void vm.setFlag(issue, e.currentTarget.checked)} />

                    <Divider />

                    <Tabs value={tab} onChange={setTab}>
                        <Tabs.List>
                            <Tabs.Tab value="conversation" leftSection={<MessagesSquare size={14} />}>
                                Conversation
                            </Tabs.Tab>
                            <Tabs.Tab value="notes" leftSection={<Paperclip size={14} />}>
                                Notes{noteCount + docCount > 0 ? ` (${noteCount + docCount})` : ''}
                            </Tabs.Tab>
                            <Tabs.Tab value="audit" leftSection={<ScrollText size={14} />}>
                                Audit{eventCount > 0 ? ` (${eventCount})` : ''}
                            </Tabs.Tab>
                        </Tabs.List>

                        <Tabs.Panel value="conversation" pt="md">
                            <Group justify="flex-end" mb="xs">
                                <Button size="compact-xs" variant="light" leftSection={<CornerDownRight size={13} />} onClick={() => setScrollSignal((n) => n + 1)}>
                                    Jump to flagged turn
                                </Button>
                            </Group>
                            <ConversationViewer sessionId={issue.sessionId} focusTurnIndex={issue.turnIndex} accent="indigo" highlightAccent="rose" autoScroll={false} scrollSignal={scrollSignal} />
                        </Tabs.Panel>

                        <Tabs.Panel value="notes" pt="md">
                            <Stack gap="xs">
                                {noteCount > 0 ? (
                                    detail?.notes.map((n) => (
                                        <Box key={n.id} p="xs" style={{ background: 'var(--bm-color-surface-muted)', borderRadius: 8 }}>
                                            <Text size="xs">{n.body}</Text>
                                            <Text size="10px" c="dimmed" mt={4}>
                                                {vm.emailFor(n.createdBy)} · {formatWhen(n.createdAt)}
                                            </Text>
                                        </Box>
                                    ))
                                ) : (
                                    <Text size="xs" c="dimmed">
                                        No notes yet.
                                    </Text>
                                )}
                                <Group align="flex-end" gap="xs">
                                    <Textarea flex={1} size="xs" autosize minRows={1} placeholder="Document how this was handled…" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
                                    <Button size="xs" disabled={note.trim().length === 0} onClick={() => { void vm.addNote(note.trim()); setNote(''); }}>
                                        Add note
                                    </Button>
                                </Group>

                                {docCount > 0 ? (
                                    <Stack gap={4} mt={4}>
                                        {detail?.attachments.map((a) => (
                                            <Group key={a.id} justify="space-between" gap="xs">
                                                <Group gap={6} wrap="nowrap">
                                                    <Paperclip size={13} />
                                                    <Anchor size="xs" component="button" type="button" onClick={() => void downloadAttachment(a)}>
                                                        {a.filename}
                                                    </Anchor>
                                                    <Text size="10px" c="dimmed">
                                                        {formatBytes(a.sizeBytes)}
                                                    </Text>
                                                </Group>
                                                <Button size="compact-xs" variant="subtle" leftSection={<Download size={12} />} onClick={() => void downloadAttachment(a)}>
                                                    Download
                                                </Button>
                                            </Group>
                                        ))}
                                    </Stack>
                                ) : null}
                                <FileButton onChange={(file) => file && void vm.addAttachment(file)}>
                                    {(props) => (
                                        <Button {...props} size="compact-xs" variant="light" w="fit-content" leftSection={<Upload size={13} />}>
                                            Upload file
                                        </Button>
                                    )}
                                </FileButton>
                            </Stack>
                        </Tabs.Panel>

                        <Tabs.Panel value="audit" pt="md">
                            {eventCount > 0 ? (
                                <Timeline active={eventCount} bulletSize={10} lineWidth={2}>
                                    {detail?.events.map((e) => (
                                        <Timeline.Item key={e.id} title={<Text size="xs" fw={600}>{EVENT_LABEL[e.kind]}</Text>}>
                                            {eventDetail(e) ? (
                                                <Text size="xs" c="dimmed">
                                                    {eventDetail(e)}
                                                </Text>
                                            ) : null}
                                            <Text size="10px" c="dimmed" mt={2}>
                                                {vm.emailFor(e.actorUserId)} · {formatWhen(e.createdAt)}
                                            </Text>
                                        </Timeline.Item>
                                    ))}
                                </Timeline>
                            ) : (
                                <Text size="xs" c="dimmed">
                                    No activity yet.
                                </Text>
                            )}
                        </Tabs.Panel>
                    </Tabs>
                </Stack>
            )}
        </Drawer>
    );
}
